import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Check, Upload, Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { requiredField } from "../lib/validation";
// The specialty/country catalogue and the stored-value -> label resolution. The option
// VALUE is the frozen wire/DB string and the LABEL is resolved per render; see
// i18n/practiceTaxonomy.js for why the stored form cannot be changed here.
import { SPECIALTIES, COUNTRIES, resolveSpecialty, resolveCountry } from "../i18n/practiceTaxonomy";
import { adminHref } from "./adminPath";

// A stored value outside the catalogue (free text set through another surface) still needs
// an option to sit on, or the picker would read "nothing selected" while the tenant
// document holds something. Its own text is its label — there is nothing to translate.
const withCurrentValue = (options, resolved) =>
  resolved.labelKey || !resolved.text
    ? options
    : [...options, { code: resolved.text, stored: resolved.text, labelKey: null }];

const STEPS = [
  { labelKey: "onboarding.steps.profile" },
  { labelKey: "onboarding.steps.specialty" },
  { labelKey: "onboarding.steps.branding" },
  { labelKey: "onboarding.steps.summary" },
];

// tenant_self_service.py's TenantOnboardingUpdate has NO backend-enforced requirement on
// any of these fields (all Optional[str]) — this is a PRODUCT rule (the wizard has always
// refused to advance past a blank phone/bio/specialty/country), previously enforced by
// disabling the Next button on content validity. That is the house-rule violation this
// fix removes: the button now stays enabled and the SAME rule is enforced on click, with
// an inline error pointing at the empty field, per step.
const STEP_SCHEMAS = {
  0: z.object({
    phone: requiredField(30),
    bio: requiredField(2000),
  }),
  1: z.object({
    specialty: z.string().min(1, "validation.required"),
    country: z.string().min(1, "validation.required"),
  }),
};

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const t = useT("admin");
  const tc = useT("common");
  const [step, setStep] = useState(0);
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({
    phone: "",
    specialty: "",
    country: "",
    photo_url: "",
    bio: "",
    primary_color: "#0B1020",
    accent_color: "#E6B85C",
    secondary_color: "#7FB8A4",
    logo_url: "",
    hero_image_url: "",
    tagline: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const updateField = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    Object.keys(patch).forEach(clearFieldError);
  };

  useEffect(() => {
    api.get("/tenant/self/info").then((r) => {
      const t = r.data;
      setTenant(t);
      setForm((f) => ({
        ...f,
        phone: t.phone || "",
        specialty: t.specialty || "",
        country: t.country || "",
        photo_url: t.photo_url || "",
        bio: t.bio || "",
        ...(t.branding || {}),
      }));
    });
  }, []);

  const finish = async () => {
    setSubmitting(true);
    try {
      // 1. profile
      await api.post("/tenant/onboarding/complete", {
        phone: form.phone,
        specialty: form.specialty,
        country: form.country,
        photo_url: form.photo_url,
        bio: form.bio,
      });
      // 2. branding
      await api.put("/tenant/branding", {
        primary_color: form.primary_color,
        accent_color: form.accent_color,
        secondary_color: form.secondary_color,
        logo_url: form.logo_url,
        hero_image_url: form.hero_image_url,
        tagline: form.tagline,
      });
      toast.success(t("onboarding.toast.success"));
      navigate(adminHref("/admin"));
    } catch (e) {
      toast.error(extractApiError(e, t("onboarding.toast.error")));
      setSubmitting(false);
    }
  };

  const upload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url || data.file_url || (data.path ? `/api/uploads/${data.path}` : "");
      if (url) setForm((f) => ({ ...f, [field]: url }));
    } catch (e) {
      toast.error(t("onboarding.toast.uploadError"));
    }
  };

  if (!tenant) return <Loader2 className="w-6 h-6 animate-spin m-12" />;

  const specialty = resolveSpecialty(form.specialty);
  const country = resolveCountry(form.country);
  // Label of a resolved value: the dictionary in the DASHBOARD language for a catalogue
  // entry, the stored text itself for free text. This is the BUG-218 fix — the summary
  // used to interpolate `form.specialty` / `form.country`, i.e. the stored French label,
  // which no choice of dashboard language could translate.
  const label = (r) => (r.labelKey ? t(r.labelKey) : r.text || "");

  // Step-advance is validated on CLICK, the same recipe as a save — never by disabling
  // the button on content validity (house rule). A step with no schema (branding, the
  // last content step before Summary) has nothing required and always advances.
  const goNext = () => {
    const schema = STEP_SCHEMAS[step];
    if (schema && validate(schema, form)) {
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    setStep((s) => s + 1);
  };

  return (
    <div data-testid="onboarding-wizard" className="min-h-screen bg-sol-ivory">
      <div className="max-w-3xl mx-auto py-12 px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <img src="/brand/solunary-logo.png" alt="Solunary" className="h-10 w-10" />
          <div>
            <div className="font-sol text-lg text-sol-midnight">{t("onboarding.welcome.title")}</div>
            <div className="text-xs text-eh-muted">{t("onboarding.welcome.subtitle")}</div>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 mb-12">
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center gap-2">
                <div
                  data-testid={`step-${i}`}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition ${
                    i < step ? "bg-sol-sage text-white" :
                    i === step ? "bg-sol-gold text-sol-midnight" :
                    "bg-eh-border-soft text-eh-muted"
                  }`}
                >
                  {i < step ? <Check className="w-5 h-5" /> : i + 1}
                </div>
                <span className={`text-xs ${i === step ? "text-sol-midnight font-medium" : "text-eh-muted"}`}>{t(s.labelKey)}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 ${i < step ? "bg-sol-sage" : "bg-eh-border-soft"} mt-[-1.25rem]`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-eh-border-soft p-8 shadow-sm">
          {/* STEP 0 — Profil */}
          {step === 0 && (
            <div className="space-y-5">
              <h2 className="font-sol text-2xl text-sol-midnight">{t("onboarding.step0.title")}</h2>
              <p className="text-eh-muted text-sm">{t("onboarding.step0.subtitle")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("onboarding.step0.phone.label")}</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => updateField({ phone: e.target.value })}
                    data-testid="onb-phone"
                    placeholder={t("onboarding.step0.phone.placeholder")}
                    className={fieldErrors.phone ? invalidInputClass : ""}
                  />
                  <FieldError message={fieldErrors.phone ? tc(fieldErrors.phone) : ""} testId="onb-phone-error" />
                </div>
                <div>
                  <Label>{t("onboarding.step0.photo.label")}</Label>
                  <label className="block mt-1.5 px-3 py-2 border border-dashed border-eh-border-soft rounded-md cursor-pointer hover:bg-eh-cream/30 text-sm text-eh-muted">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "photo_url")} data-testid="onb-photo" />
                    {form.photo_url ? <img src={form.photo_url} alt="" className="h-10 w-10 rounded-full inline-block" /> : <><Upload className="w-4 h-4 inline mr-2" /> {t("onboarding.upload.generic")}</>}
                  </label>
                </div>
              </div>
              <div>
                <Label>{t("onboarding.step0.bio.label")}</Label>
                <textarea
                  value={form.bio}
                  onChange={(e) => updateField({ bio: e.target.value })}
                  data-testid="onb-bio"
                  rows={4}
                  placeholder={t("onboarding.step0.bio.placeholder")}
                  className={`w-full mt-1.5 border rounded-md p-3 text-sm resize-none ${fieldErrors.bio ? invalidInputClass : "border-eh-border-soft"}`}
                />
                <FieldError message={fieldErrors.bio ? tc(fieldErrors.bio) : ""} testId="onb-bio-error" />
              </div>
            </div>
          )}

          {/* STEP 1 — Spécialité */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-sol text-2xl text-sol-midnight">{t("onboarding.step1.title")}</h2>
              <p className="text-eh-muted text-sm">{t("onboarding.step1.subtitle")}</p>
              <div>
                <Label>{t("onboarding.step1.specialty.label")}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {withCurrentValue(SPECIALTIES, specialty).map((s) => (
                    <button
                      key={s.stored}
                      onClick={() => updateField({ specialty: s.stored })}
                      // Keyed on the CODE, not the stored label: a test id is an identifier,
                      // so it must not carry accents or move if a label is ever re-worded.
                      data-testid={`onb-spec-${s.code}`}
                      className={`px-3 py-1.5 rounded-full border text-sm transition ${
                        form.specialty === s.stored ? "bg-sol-midnight text-white border-sol-midnight" : "border-eh-border-soft hover:bg-eh-cream/40"
                      }`}
                    >
                      {s.labelKey ? t(s.labelKey) : s.stored}
                    </button>
                  ))}
                </div>
                <FieldError message={fieldErrors.specialty ? tc(fieldErrors.specialty) : ""} testId="onb-specialty-error" />
              </div>
              <div>
                <Label>{t("onboarding.step1.country.label")}</Label>
                <select
                  value={form.country}
                  onChange={(e) => updateField({ country: e.target.value })}
                  data-testid="onb-country"
                  className={`w-full mt-1.5 border rounded-md px-3 h-10 text-sm ${fieldErrors.country ? invalidInputClass : "border-eh-border-soft"}`}
                >
                  <option value="">{t("onboarding.step1.country.placeholder")}</option>
                  {withCurrentValue(COUNTRIES, country).map((c) => (
                    <option key={c.stored} value={c.stored}>{c.labelKey ? t(c.labelKey) : c.stored}</option>
                  ))}
                </select>
                <FieldError message={fieldErrors.country ? tc(fieldErrors.country) : ""} testId="onb-country-error" />
              </div>
            </div>
          )}

          {/* STEP 2 — Branding */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-sol text-2xl text-sol-midnight">{t("onboarding.step2.title")}</h2>
              <p className="text-eh-muted text-sm">{t("onboarding.step2.subtitle")}</p>
              <div>
                <Label>{t("onboarding.step2.tagline.label")}</Label>
                <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder={t("onboarding.step2.tagline.placeholder")} data-testid="onb-tagline" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {[
                  ["primary_color", t("onboarding.step2.color.primary")],
                  ["accent_color", t("onboarding.step2.color.accent")],
                  ["secondary_color", t("onboarding.step2.color.secondary")],
                ].map(([k, lbl]) => (
                  <div key={k}>
                    <Label className="text-xs">{lbl}</Label>
                    <div className="flex gap-1.5 mt-1">
                      <input type="color" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-12 h-10 rounded border border-eh-border-soft" data-testid={`onb-color-${k}`} />
                      <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="font-mono text-xs" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">{t("onboarding.step2.logo.label")}</Label>
                  <label className="block mt-1.5 px-3 py-3 border-2 border-dashed border-eh-border-soft rounded-md cursor-pointer text-center text-sm text-eh-muted hover:bg-eh-cream/30">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "logo_url")} data-testid="onb-logo" />
                    {form.logo_url ? <img src={form.logo_url} alt="" className="h-12 mx-auto" /> : t("onboarding.upload.logo")}
                  </label>
                </div>
                <div>
                  <Label className="text-xs">{t("onboarding.step2.hero.label")}</Label>
                  <label className="block mt-1.5 px-3 py-3 border-2 border-dashed border-eh-border-soft rounded-md cursor-pointer text-center text-sm text-eh-muted hover:bg-eh-cream/30">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "hero_image_url")} data-testid="onb-hero" />
                    {form.hero_image_url ? <img src={form.hero_image_url} alt="" className="h-12 mx-auto" /> : t("onboarding.upload.hero")}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Récap */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-sol text-2xl text-sol-midnight">{t("onboarding.step3.title")}</h2>
              <p className="text-eh-muted text-sm">{t("onboarding.step3.subtitle")}</p>
              <div className="rounded-2xl p-6" style={{ background: form.primary_color, color: "#fff" }}>
                <div className="flex items-center gap-3 mb-3">
                  {form.logo_url && <img src={form.logo_url} alt="" className="h-10 w-10 rounded" />}
                  <div>
                    <div className="text-xl font-semibold">{tenant.name}</div>
                    <div className="text-sm" style={{ color: form.accent_color }}>{form.tagline}</div>
                  </div>
                </div>
                <div className="text-xs opacity-70 space-y-0.5">
                  <div>{t("onboarding.step3.specialty.label")} {label(specialty)}</div>
                  <div>{t("onboarding.step3.country.label")} {label(country)}</div>
                  <div>{t("onboarding.step3.phone.label")} {form.phone}</div>
                </div>
              </div>
            </div>
          )}

          {/* NAV */}
          <div className="flex justify-between gap-3 mt-8 pt-6 border-t border-eh-border-soft">
            {step > 0 ? (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)} data-testid="onb-prev">
                {tc("actions.previous")}
              </Button>
            ) : <span />}
            {step < STEPS.length - 1 ? (
              <Button
                onClick={goNext}
                data-testid="onb-next"
                className="bg-sol-midnight text-white hover:bg-sol-midnight/90"
              >
                {t("onboarding.nav.next")} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={finish}
                disabled={submitting}
                data-testid="onb-finish"
                className="bg-sol-gold text-sol-midnight hover:bg-sol-gold/90"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {t("onboarding.nav.finish")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
